import { __assign, __awaiter, __generator, __read } from "tslib";
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { useMutation, useQueryCache, } from '@office-start/data-fetching';
import { fetchEdgeworthConfig } from '@office-start/edgeworth-config-service';
import { fetchIdentity } from '@office-start/identity-service';
import { setUserItem } from '@office-start/web-storage-utility';
import { useMemo, useRef } from 'react';
import { recentQueryKey, SettingsCacheKeyPrefix } from '../models/Constants';
import { deleteSetting } from '../operations/deleteSetting';
import { getBaseRequestData } from '../operations/getBaseRequestData';
import { upsertSetting } from '../operations/upsertSetting';
export function useMutateEdgeworthSetting(onUpsertSetting, onDeleteSetting, onUpsertSettingError, onDeleteSettingError) {
    var _this = this;
    var queryCache = useQueryCache();
    var dependenciesPromise = useMemo(function () {
        return Promise.all([
            fetchOfficeComponentContext(),
            fetchIdentity(),
            fetchEdgeworthConfig(),
        ]);
    }, []);
    var identityRef = useRef();
    var upsertMutation = useMutation(function (setting) { return __awaiter(_this, void 0, void 0, function () {
        var _a, context, identity, config, upsertSettingRequest, apiOptions;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, dependenciesPromise];
                case 1:
                    _a = __read.apply(void 0, [_b.sent(), 3]), context = _a[0], identity = _a[1], config = _a[2];
                    identityRef.current = identity;
                    // TODO: Should config-resolver-service resolveConfig throw rather than returning undefined?
                    if (!config) {
                        return [2 /*return*/, Promise.reject('Missing EdgeworthConfig')];
                    }
                    upsertSettingRequest = {
                        setting_name: setting.name,
                        setting_value: setting.value,
                    };
                    apiOptions = __assign(__assign({}, getBaseRequestData(context, identity, config)), { setting: upsertSettingRequest, upsertApiPath: config.upsertSettingApiPath, preferredCulture: context.preferredCulture });
                    return [2 /*return*/, upsertSetting(apiOptions)];
            }
        });
    }); }, {
        onMutate: function (setting) {
            if (onUpsertSetting) {
                onUpsertSetting(setting);
            }
            return setting;
        },
        onSuccess: function (_data, setting) {
            var _a;
            // Update recent data in memory and avoid refetching recent API. We can't pass a callback
            // directly from caller because the scope would have changed after onMutate fires. Same is done
            // for deleteMutation's onSuccess below
            if (queryCache) {
                var recentData = queryCache.getQueryData(recentQueryKey);
                if (recentData) {
                    // If setting already exists, filter it out first since we are upserting
                    var newSettings = recentData.settings.filter(function (val) { return val.name !== setting.name; });
                    // New settings are always added to top of list
                    newSettings.unshift(setting);
                    queryCache.setQueryData(recentQueryKey, __assign(__assign({}, recentData), { settings: newSettings }));
                    if (identityRef.current) {
                        setUserItem((_a = identityRef.current) === null || _a === void 0 ? void 0 : _a.puid, SettingsCacheKeyPrefix, newSettings);
                    }
                }
            }
        },
        onError: function (_error, setting) {
            if (onUpsertSettingError) {
                onUpsertSettingError(setting);
            }
        },
    });
    var deleteMutation = useMutation(function (setting) { return __awaiter(_this, void 0, void 0, function () {
        var _a, context, identity, config, deleteSettingRequest, apiOptions;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0: return [4 /*yield*/, dependenciesPromise];
                case 1:
                    _a = __read.apply(void 0, [_b.sent(), 3]), context = _a[0], identity = _a[1], config = _a[2];
                    identityRef.current = identity;
                    // TODO: Should config-resolver-service resolveConfig throw rather than returning undefined?
                    if (!config) {
                        return [2 /*return*/, Promise.reject('Missing EdgeworthConfig')];
                    }
                    deleteSettingRequest = {
                        setting_name: setting.name,
                    };
                    apiOptions = __assign(__assign({}, getBaseRequestData(context, identity, config)), { setting: deleteSettingRequest, deleteApiPath: config.deleteSettingApiPath, preferredCulture: context.preferredCulture });
                    return [2 /*return*/, deleteSetting(apiOptions)];
            }
        });
    }); }, {
        onMutate: function (setting) {
            if (onDeleteSetting) {
                onDeleteSetting(setting);
            }
            return setting;
        },
        onSuccess: function (_data, setting) {
            var _a;
            if (queryCache) {
                var recentData = queryCache.getQueryData(recentQueryKey);
                if (recentData) {
                    var newSettings = recentData.settings.filter(function (val) { return val.name !== setting.name; });
                    queryCache.setQueryData(recentQueryKey, __assign(__assign({}, recentData), { settings: newSettings }));
                    if (identityRef.current) {
                        setUserItem((_a = identityRef.current) === null || _a === void 0 ? void 0 : _a.puid, SettingsCacheKeyPrefix, newSettings);
                    }
                }
            }
        },
        onError: function (_error, setting) {
            if (onDeleteSettingError) {
                onDeleteSettingError(setting);
            }
        },
    });
    return useMemo(function () { return ({
        upsertMutation: upsertMutation,
        deleteMutation: deleteMutation,
    }); }, [deleteMutation, upsertMutation]);
}
//# sourceMappingURL=useMutateEdgeworthSetting.js.map